// The person's own document, `users/{uid}`: their favorites, which the apps write, and what the server and the
// account's marks put there, which the web only reads (FirestoreBackend.swift: UserDocument and the user listener;
// Models.swift: Favorite).
//
// The apps write nothing here but `favorites` (with merge), the marks, `interfaceLanguage` and `areaIconsAddedAt`
// (docs/61); everything else on the
// document belongs to the server or to other settings, and stays untouched (docs/47, rule 8).

import { readRecord } from "./shared";
import {
  associatedValue, DecodeFailure, enumCase, isPlainObject, readString, readUUID,
  type DecodeContext, type Fields, type ValueReader,
} from "./values";

/**
 * A place pinned to the dashboard: a project, an area, a built-in list by its storage key ("today"), or a saved place.
 * A kind from a newer version is kept as stored and shown nowhere: without it, one such favorite would make the whole
 * list unreadable, and the next change here would erase the newer ones.
 */
export type Favorite =
  | { kind: "project"; id: string }
  | { kind: "area"; id: string }
  | { kind: "list"; key: string }
  | { kind: "place"; id: string }
  | { kind: "unknown"; raw: unknown };

export interface UserDocument {
  /** Absent when the document has none, or holds a list this version can't read: the apps then keep what they had. */
  favorites?: Favorite[];
  /** Below this feature level a version writes nothing (`markAccount(strictly: true)`). */
  minimumFeatureLevel?: number;
  /** Below this feature level a version suggests the update. */
  suggestedFeatureLevel?: number;
  /** The same for versions before feature levels (iPhone 1.0): "1.1.0". */
  minimumVersion?: string;
  suggestedVersion?: string;
  /** Every day of the account is stored the new way (docs/46). */
  datesConverted: boolean;
  /** Pro as the server last found it (`premium.active`); for the interface only, the server decides. */
  premiumActive?: boolean;
  /**
   * The language the device last used showed its interface in ("de", "en"): the server's own texts follow it while the
   * account's language is "Automatic" (Bastian, 2026-10-20). Every app writes it when it differs.
   */
  interfaceLanguage?: string;
  /**
   * The account's areas got their icons once (`areaIconsAddedAt`, a server time; docs/61): no device does it again. Set
   * when the field is there at all, also while its server time is still on its way (it reads as null then).
   */
  areaIconsAdded?: true;
  /** The rest of the document, as it is. Never written back by the web. */
  unknownFields: Fields;
}

export const USER_FIELDS = [
  "favorites", "minimumFeatureLevel", "suggestedFeatureLevel", "minimumVersion", "suggestedVersion", "datesConverted",
  "interfaceLanguage", "areaIconsAddedAt",
] as const;

const FAVORITE_CASES = ["project", "area", "list", "place"] as const;

/** A favorite: one of the known cases in Swift's shape (`{"project": {"_0": id}}`), or anything else, kept. */
const readFavorite: ValueReader<Favorite> = (value, path, context) => {
  // A null in the list is refused, as Swift's decoder refuses it for a non-optional element.
  if (value === null || value === undefined) throw new DecodeFailure(`${path} has the wrong shape`);
  try {
    const found = enumCase(value, FAVORITE_CASES);
    switch (found?.name) {
      case "project": return { kind: "project", id: associatedValue(found.payload, readUUID, path, context) };
      case "area": return { kind: "area", id: associatedValue(found.payload, readUUID, path, context) };
      case "list": return { kind: "list", key: associatedValue(found.payload, readString, path, context) };
      case "place": return { kind: "place", id: associatedValue(found.payload, readUUID, path, context) };
      case undefined: break;
    }
  } catch (error) {
    if (!(error instanceof DecodeFailure)) throw error;
  }
  return { kind: "unknown", raw: value };
};

export function encodeFavorite(favorite: Favorite): unknown {
  switch (favorite.kind) {
    case "project": return { project: { _0: favorite.id } };
    case "area": return { area: { _0: favorite.id } };
    case "list": return { list: { _0: favorite.key } };
    case "place": return { place: { _0: favorite.id } };
    case "unknown": return favorite.raw;
  }
}

/** The user's document as the apps read it. Never fails: what can't be read is left out. */
export function decodeUserDocument(data: Fields | undefined, context: DecodeContext): UserDocument {
  const fields = data ?? {};
  return readRecord(fields, context, (reader) => {
    const result: UserDocument = { datesConverted: fields.datesConverted === true, unknownFields: reader.unknownFields(USER_FIELDS) };
    try {
      const favorites = reader.optional("favorites", (value, path, ctx) => {
        if (!Array.isArray(value)) throw new DecodeFailure(`${path} has the wrong shape`);
        return value.map((item, index) => readFavorite(item, `${path}[${index}]`, ctx));
      });
      if (favorites !== undefined) result.favorites = favorites;
    } catch (error) {
      if (!(error instanceof DecodeFailure)) throw error;
    }
    const level = (key: string) => (Number.isInteger(fields[key]) ? (fields[key] as number) : undefined);
    const text = (key: string) => (typeof fields[key] === "string" ? (fields[key] as string) : undefined);
    const minimumFeatureLevel = level("minimumFeatureLevel");
    if (minimumFeatureLevel !== undefined) result.minimumFeatureLevel = minimumFeatureLevel;
    const suggestedFeatureLevel = level("suggestedFeatureLevel");
    if (suggestedFeatureLevel !== undefined) result.suggestedFeatureLevel = suggestedFeatureLevel;
    const minimumVersion = text("minimumVersion");
    if (minimumVersion !== undefined) result.minimumVersion = minimumVersion;
    const suggestedVersion = text("suggestedVersion");
    if (suggestedVersion !== undefined) result.suggestedVersion = suggestedVersion;
    const interfaceLanguage = text("interfaceLanguage");
    if (interfaceLanguage !== undefined) result.interfaceLanguage = interfaceLanguage;
    if ("areaIconsAddedAt" in fields) result.areaIconsAdded = true;
    const premium = fields.premium;
    if (isPlainObject(premium) && typeof premium.active === "boolean") result.premiumActive = premium.active;
    return result;
  });
}

/** What the apps write when the favorites change: only `favorites`, merged into the document. */
export function encodeFavorites(favorites: readonly Favorite[]): Fields {
  return { favorites: favorites.map(encodeFavorite) };
}
